import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Columns3 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mono } from "@/components/Badges";
import { fmtDate, fmtMoney } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { useCustomer } from "@/lib/customer";

const DOT = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  grey: "bg-muted-foreground",
};

const Card = ({ po, onOpen, customerName }) => (
  <button
    onClick={() => onOpen(po)}
    className="w-full rounded-md border border-border bg-background p-3 text-left transition-colors hover:border-primary hover:bg-accent"
    data-testid={`board-card-${po.key}`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{po.branchName || "—"}</span>
        <Mono className="block truncate text-[11px] text-muted-foreground">
          {po.poNumber} · {po.branchCode || "—"}
        </Mono>
      </span>
      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${DOT[po.ldColour] || DOT.grey}`} title={po.ldStatus} />
    </div>
    {customerName && (
      <div className="mt-1">
        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium" data-testid={`board-customer-${po.key}`}>
          {customerName}
        </span>
      </div>
    )}
    {(po.ldMailPending || po.bomPending) && (
      <div className="mt-2 flex flex-wrap gap-1">
        {po.ldMailPending && (
          <span
            className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
            data-testid={`board-ld-pending-${po.key}`}
          >
            LD mail pending
          </span>
        )}
        {po.bomPending && (
          <span
            className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
            data-testid={`board-bom-pending-${po.key}`}
          >
            BOM pending
          </span>
        )}
      </div>
    )}
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
      <span>{po.lineCount} line{po.lineCount > 1 ? "s" : ""}</span>
      <span>·</span>
      <span>{fmtMoney(po.value)}</span>
      {po.daysInStage !== null && (
        <>
          <span>·</span>
          <span data-testid={`board-days-${po.key}`}>{po.daysInStage}d here</span>
        </>
      )}
    </div>
    {po.expectedMaterialReadyDate && (
      <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`board-material-date-${po.key}`}>
        material wanted by <Mono>{fmtDate(po.expectedMaterialReadyDate)}</Mono>
      </div>
    )}
    <div className="mt-1 text-[11px] text-muted-foreground">
      since <Mono>{fmtDate(po.enteredOn)}</Mono>
    </div>
  </button>
);

export default function Board() {
  const { user } = useAuth();
  const { isAllCustomers, customerName, customerId, onExpiryClock } = useCustomer();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [owner, setOwner] = useState("__mine");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pending, setPending] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (owner === "__mine") params.mine = true;
    else if (owner) params.owner = owner;
    if (from) params.fromDate = from;
    if (to) params.toDate = to;
    if (pending) params.pending = pending;
    try {
      const { data: res } = await api.get("/board", { params });
      setData(res);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [owner, from, to, pending, customerId]);

  useEffect(() => {
    load();
    const refresh = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  useEffect(() => {
    api.get("/meta").then(({ data: m }) => setPeople(m.people)).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="board-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight sm:text-3xl">
          <Columns3 className="h-6 w-6" />
          Board
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every PO in the column of the stage it is on. Record an update and the card moves on. Correct a wrong entry
          and it moves back.
        </p>
      </div>

      <div className="panel flex flex-wrap items-end gap-3 p-3">
        <div>
          <Label className="field-label">Whose POs</Label>
          <select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="board-owner-filter"
          >
            <option value="__mine">{`Only mine (${user?.name || ""})`}</option>
            <option value="">Everyone</option>
            {people.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value="Unassigned">Unassigned</option>
          </select>
        </div>
        <div>
          <Label className="field-label">Show</Label>
          <select
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="board-pending-filter"
          >
            <option value="">All POs</option>
            {!onExpiryClock && <option value="ld">LD mail pending</option>}
            {!onExpiryClock && <option value="bom">BOM pending</option>}
            <option value="material">Material readiness date set</option>
          </select>
        </div>
        <div>
          <Label className="field-label">PO date from</Label>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-40 font-mono tabular-nums"
            data-testid="board-from-input"
          />
        </div>
        <div>
          <Label className="field-label">to</Label>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 w-40 font-mono tabular-nums"
            data-testid="board-to-input"
          />
        </div>
        {(from || to) && (
          <button
            onClick={() => {
              setFrom("");
              setTo("");
            }}
            className="pb-2 text-xs text-primary hover:underline"
            data-testid="board-clear-dates"
          >
            Clear the dates
          </button>
        )}
        <span className="ml-auto pb-2 text-xs text-muted-foreground" data-testid="board-total">
          {data ? `${data.total} PO${data.total === 1 ? "" : "s"} on the board` : "Loading…"}
        </span>
      </div>

      {data && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="board-columns">
          {data.columns.map((col) => (
            <div
              key={`${col.customerId || ""}-${col.stage}`}
              className="panel flex w-[260px] shrink-0 flex-col p-3"
              data-testid={`board-column-${col.stage}`}
            >
              <div className="flex items-baseline justify-between gap-2 border-b border-border pb-2">
                <h2 className="text-sm font-medium leading-tight">{col.label}</h2>
                <Mono className="text-xs text-muted-foreground" data-testid={`board-count-${col.stage}`}>
                  {col.count}
                </Mono>
              </div>
              <div className="mt-3 space-y-2">
                {col.pos.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">Nothing here</p>
                ) : (
                  col.pos.map((po) => (
                    <Card
                      key={po.key}
                      po={po}
                      customerName={isAllCustomers ? customerName(po.customerId) : ""}
                      onOpen={(p) => navigate(`/po-history/${p.lineId}`)}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
